/** Discrete views/likes range steps used by CFV4 performance sliders. */
export const PERFORMANCE_RANGE_STEPS = [
  0,
  1000,
  3000,
  5000,
  10000,
  15000,
  20000,
  25000,
  35000,
  50000,
  75000,
  100000,
  125000,
  150000,
  175000,
  200000,
  250000,
  300000,
  350000,
  500000,
  1000000,
  2000000,
  3000000,
] as const;

export const PERFORMANCE_RANGE_TICK_LABELS = [
  '0',
  '10K',
  '50K',
  '150K',
  '500K',
  '1M',
  '3M',
] as const;

export const PERFORMANCE_RANGE_TICK_VALUES = [
  0,
  10000,
  50000,
  150000,
  500000,
  1000000,
  3000000,
] as const;

export type PerformanceRangeValue = { min: number; max: number };

export const PERFORMANCE_RANGE_DEFAULT: PerformanceRangeValue = {
  min: PERFORMANCE_RANGE_STEPS[0],
  max: PERFORMANCE_RANGE_STEPS[PERFORMANCE_RANGE_STEPS.length - 1],
};

export const PERFORMANCE_RANGE_MAX_INDEX = PERFORMANCE_RANGE_STEPS.length - 1;

export function performanceStepIndexToValue(index: number): number {
  const clamped = Math.max(0, Math.min(Math.round(index), PERFORMANCE_RANGE_MAX_INDEX));
  return PERFORMANCE_RANGE_STEPS[clamped];
}

export function performanceValueToStepIndex(value: number): number {
  if (!Number.isFinite(value) || value <= PERFORMANCE_RANGE_STEPS[0]) {
    return 0;
  }
  if (value >= PERFORMANCE_RANGE_STEPS[PERFORMANCE_RANGE_MAX_INDEX]) {
    return PERFORMANCE_RANGE_MAX_INDEX;
  }

  let nearestIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let i = 0; i < PERFORMANCE_RANGE_STEPS.length; i++) {
    const distance = Math.abs(PERFORMANCE_RANGE_STEPS[i] - value);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = i;
    }
  }
  return nearestIndex;
}

export function isDefaultPerformanceRange(
  range: PerformanceRangeValue | null | undefined,
): boolean {
  if (!range) {
    return true;
  }
  return range.min === PERFORMANCE_RANGE_DEFAULT.min && range.max === PERFORMANCE_RANGE_DEFAULT.max;
}

export function formatPerformanceStepLabel(value: number): string {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return Number.isInteger(millions) ? `${millions}M` : `${millions.toFixed(1)}M`;
  }
  if (value >= 1000) {
    const thousands = value / 1000;
    return Number.isInteger(thousands) ? `${thousands}K` : `${thousands.toFixed(1)}K`;
  }
  return `${value}`;
}

export function formatPerformanceRangeLabel(range: PerformanceRangeValue): string {
  return `${formatPerformanceStepLabel(range.min)} – ${formatPerformanceStepLabel(range.max)}`;
}

export interface EngagementRateOption {
  value: number | null;
  label: string;
}

export const ENGAGEMENT_RATE_OPTIONS: EngagementRateOption[] = [
  { value: null, label: 'Any' },
  { value: 0.5, label: '≥0.5%' },
  { value: 1, label: '≥1%' },
  { value: 2, label: '≥2% (average)' },
  { value: 3, label: '≥3%' },
  { value: 4, label: '≥4%' },
  { value: 5, label: '≥5%' },
  { value: 6, label: '≥6%' },
  { value: 7, label: '≥7%' },
  { value: 8, label: '≥8%' },
  { value: 9, label: '≥9%' },
  { value: 10, label: '≥10%' },
];

export type PerformancePlatform = 'tiktok' | 'instagram' | 'youtube';

export interface PlatformPerformanceDraft {
  averageEngagement: number | null;
  averageViews: PerformanceRangeValue | null;
  totalViews: PerformanceRangeValue | null;
  averageLikes: PerformanceRangeValue | null;
  totalLikes: PerformanceRangeValue | null;
}

export function emptyPlatformPerformanceDraft(): PlatformPerformanceDraft {
  return {
    averageEngagement: null,
    averageViews: null,
    totalViews: null,
    averageLikes: null,
    totalLikes: null,
  };
}

export function isEmptyPlatformPerformance(draft: PlatformPerformanceDraft): boolean {
  return (
    draft.averageEngagement == null &&
    draft.averageViews == null &&
    draft.totalViews == null &&
    draft.averageLikes == null &&
    draft.totalLikes == null
  );
}
